@file:Suppress("UnusedReceiverParameter")

package ui.device.detail.editor

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.staggeredgrid.LazyStaggeredGridScope
import androidx.compose.foundation.lazy.staggeredgrid.LazyVerticalStaggeredGrid
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridCells
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import api.ResourceOrientation
import compose.assets.Svg
import compose.assets.svg.*
import kotlinx.coroutines.launch
import org.ssoft.common.server.portal.PortalIO
import org.ssoft.common.tools.calculateScreenRatio
import org.ssoft.common.type.ResolutionStandard
import org.ssoft.common.type.ScreenRatio
import provider.LocalExternalsProvider
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState
import tools.SearchWrapper
import tools.rememberString
import ui.app.assign.detail.wheel.editor.widgets.AddIconAction
import ui.snackBarStateGlobal
import ui.widget.*

data class DeviceEditorArgs(
    var deviceId: Long = -1,
    var registrationDeviceKey: String = ""
)

@ComposableNavNode(DeviceEditorArgs::class)
@Composable
fun DeviceEditorUI() {
    with(DeviceEditorScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrorView(state.message)
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> {
                Box(Modifier.fillMaxSize()) {
                    Content(state.nnvl)
                    if (vm.isLoading) LoadingOverlayUI()
                }
            }
        }
    }
}

@Composable
private fun DeviceEditorScope.ErrorView(msg: String) {
    val registrationMode = remember { args.registrationDeviceKey.isNotEmpty() }
    Column(Modifier.fillMaxSize()) {
        Toolbar(
            nav = nav,
            titlePrefix =
                if (registrationMode) Res.string.device_listing__device_registration__title.rememberString()
                else Res.string.device_detail_edit__title.rememberString(),
            titleSuffix =
                if (registrationMode) ""
                else "(${args.deviceId})"
        )
        Text(
            text =
                if (registrationMode) "Registration failed, check the device's internet connection and try again."
                else msg,
            modifier = Modifier.fillMaxSize()
                .padding(32.dp),
            textAlign = TextAlign.Center
        )
    }
}

@Composable
fun DeviceEditorScope.Content(nnvl: DeviceEditorScope.Wrapper) {
    val saveState = remember {
        derivedStateOf {
            nnvl.change
        }
    }
    Column(Modifier.fillMaxSize()) {
        Toolbar(
            nav = nav,
            titlePrefix =
                if (nnvl.registrationMode) Res.string.device_listing__device_registration__title.rememberString()
                else Res.string.device_detail_edit__title.rememberString(),
            titleSuffix =
                if (nnvl.registrationMode) ""
                else "(${args.deviceId})",
            actions = listOf(
                ToolbarAction.save(saveState) { action(DeviceEditorVM.Action.Save) }
            )
        )

        LazyVerticalStaggeredGrid(
            columns = StaggeredGridCells.FixedSize(512.dp),
            modifier = Modifier.fillMaxSize(),
            verticalItemSpacing = 16.dp,
            horizontalArrangement = Arrangement.SpaceAround,
            contentPadding = PaddingValues(
                start = 16.dp,
                end = 16.dp,
                top = 16.dp,
                bottom = 32.dp
            )
        ) {
            Item {
                Title(nnvl, Modifier.fillMaxWidth())
                Locality(nnvl, Modifier.fillMaxWidth())
                Layers(nnvl, Modifier.fillMaxWidth())
                DefaultKioskApp(nnvl, Modifier.fillMaxWidth())
            }
            Item {
                RotationScreen(nnvl, Modifier.fillMaxWidth())
                ResolutionScreen(nnvl, Modifier.fillMaxWidth())
                RatioSetup(nnvl, Modifier.fillMaxWidth())
                RotationTouch(nnvl, Modifier.fillMaxWidth())
                VideoScale(nnvl, Modifier.fillMaxWidth())
            }
            Item {
                HardwareItem(nnvl, Modifier.fillMaxWidth())
                DevicePhoto(nnvl, Modifier.fillMaxWidth())
            }
            Item {
                GeoLocation(nnvl, Modifier.fillMaxWidth())
                Volume(nnvl, Modifier.fillMaxWidth())
                Cursor(nnvl, Modifier.fillMaxWidth())
            }
        }
    }
}

private fun LazyStaggeredGridScope.Item(body: @Composable ColumnScope.() -> Unit) {
    item {
        Card(
            colors = CardDefaults.cardColors(
                containerColor = MaterialTheme.colorScheme.surfaceContainer
            )
        ) {
            Column(
                modifier = Modifier.fillMaxWidth().padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp),
                content = body
            )
        }
    }
}

@Composable
fun DeviceEditorScope.HardwareItem(
    nnvl: DeviceEditorScope.Wrapper,
    modifier: Modifier
) {
    var isMenuExpanded by remember {
        mutableStateOf(false)
    }

    EditorCardWrapper(
        icon = Svg.Device,
        title = Res.string.device_detail__hardware_title,
        tooltip = Res.string.device_detail__hardware_tooltip,
        modifier = modifier
    ) {
        ElevatedCard(
            onClick = { isMenuExpanded = !isMenuExpanded },
            colors = CardDefaults.elevatedCardColors(
                containerColor = MaterialTheme.colorScheme.surfaceContainerHigh
            )
        ) {
            nnvl.data.hardware.firstOrNull { it.id == nnvl.hardwareId.state }?.also { entry ->
                HardwareEntry(
                    false,
                    entry.image,
                    entry.name,
                    entry.subtitle
                )
            } ?: run {
                HardwareEntry(
                    false,
                    -1L,
                    "Undefined",
                    "Select correct type"
                )
            }
            DropdownMenu(
                expanded = isMenuExpanded,
                onDismissRequest = { isMenuExpanded = false }) {

                nnvl.data.hardware.forEach {
                    DropdownMenuItem(
                        text = {
                            HardwareEntry(
                                largeImage = true,
                                imageRes = it.image,
                                title = it.name,
                                subtitle = it.subtitle
                            )
                        },
                        onClick = {
                            nnvl.hardwareId.set(it.id)
                            isMenuExpanded = false
                        })

                }
            }
        }
    }

}


@Composable
fun DeviceEditorScope.RatioSetup(
    nnvl: DeviceEditorScope.Wrapper,
    modifier: Modifier
) {

    fun ScreenRatio.toProp(): DeviceEditorScope.Wrapper.ScreenRationProp {
        return DeviceEditorScope.Wrapper.ScreenRationProp(this)
    }

    val currentRatioH = remember {
        val r = ScreenRatio[nnvl.screenRatio.state]
        if (r.land) mutableStateOf(r.toProp())
        else mutableStateOf(r.reverse().toProp())
    }

    val currentRatioV = remember {
        val r = ScreenRatio[nnvl.screenRatio.state]
        if (r.port) mutableStateOf(r.toProp())
        else mutableStateOf(r.reverse().toProp())
    }

    EditorCardWrapper(
        icon = Svg.Aspect_ratio,
        title = Res.string.device_detail__display__aspect_ratio_title,
        tooltip = Res.string.device_detail__display__aspect_ratio_tooltip,
        modifier = modifier
    ) {

        if (nnvl.screenWidth.state > nnvl.screenHeight.state) {
            DropDownPicker(
                enabled = nnvl.canEditDisplay,
                selected = currentRatioH,
                values = DeviceEditorScope.Wrapper.ScreenRationProp.allHorizontal,
                cardModifier = Modifier.fillMaxWidth(),
                previewImage = Svg.Relative_scale,
                itemWidth = 384.dp,
                colors = CardDefaults.elevatedCardColors(
                    containerColor = MaterialTheme.colorScheme.surfaceContainerHigh
                )
            ) {
                nnvl.screenRatio.set(it.type.mask)
            }
        } else {
            DropDownPicker(
                enabled = nnvl.canEditDisplay,
                selected = currentRatioV,
                values = DeviceEditorScope.Wrapper.ScreenRationProp.allVertical,
                previewImage = Svg.Relative_scale,
                cardModifier = Modifier.fillMaxWidth(),
                itemWidth = 384.dp,
                colors = CardDefaults.elevatedCardColors(
                    containerColor = MaterialTheme.colorScheme.surfaceContainerHigh
                )
            ) {
                nnvl.screenRatio.set(it.type.mask)
            }
        }
    }

    LaunchedEffect(nnvl.screenWidth.state, nnvl.screenHeight.state) {
        val ratio = calculateScreenRatio(nnvl.screenWidth.state, nnvl.screenHeight.state)
            .let {
                if (it !is ScreenRatio.NONE) it
                else if (nnvl.screenWidth.state > nnvl.screenHeight.state) ScreenRatio._16_9
                else ScreenRatio._9_16
            }
        if (ratio.land) {
            currentRatioH.value = ratio.toProp()
            currentRatioV.value = ratio.reverse().toProp()
        } else {
            currentRatioH.value = ratio.reverse().toProp()
            currentRatioV.value = ratio.toProp()
        }
        println("R ${ratio.mask}")
        nnvl.screenRatio.set(ratio.mask)
    }


}

@Composable
fun DeviceEditorScope.ResolutionScreen(
    nnvl: DeviceEditorScope.Wrapper,
    modifier: Modifier
) {

    EditorCardWrapper(
        icon = Svg.Aspect_ratio,
        title = Res.string.device_detail__display__resolution_title,
        tooltip = Res.string.device_detail__display__resolution_tooltip,
        modifier = modifier
    ) {
        ElevatedCard(
            colors = CardDefaults.elevatedCardColors(
                containerColor = if (nnvl.canEditDisplay) MaterialTheme.colorScheme.surfaceContainerHigh
                else MaterialTheme.colorScheme.surfaceContainerLowest
            )
        ) {
            DisplayEditor(nnvl, Modifier.padding(16.dp))
        }
    }
}


@Composable
fun DeviceEditorScope.RotationTouch(
    nnvl: DeviceEditorScope.Wrapper,
    modifier: Modifier
) {
    var isMenuExpanded by remember {
        mutableStateOf(false)
    }
    val animation by animateFloatAsState(
        animationSpec = tween(200),
        targetValue = if (isMenuExpanded) 180F
        else 0F
    )
    EditorCardWrapper(
        icon = Svg.Cursor_pointer,
        title = Res.string.device_detail__touch__rotation_title,
        tooltip = Res.string.device_detail__touch__rotation_tooltip,
        modifier = modifier
    ) {
        ElevatedCard(
            enabled = nnvl.canEditDisplay,
            onClick = { isMenuExpanded = !isMenuExpanded },
            colors = CardDefaults.elevatedCardColors(
                containerColor = MaterialTheme.colorScheme.surfaceContainerHigh
            )
        ) {
            Row(Modifier.fillMaxWidth().padding(16.dp)) {
                with(
                    DeviceEditorScope.Wrapper.RotationTouch.all
                        .firstOrNull { it.type match nnvl.rotationTouch.state }
                        ?: DeviceEditorScope.Wrapper.RotationTouch.all.first()) {
                    Icon(icon, "", Modifier.rotate(angle.toFloat()))
                    Space(16.dp)
                    Text(
                        shortcut.rememberString(),
                        modifier = Modifier.weight(1F)
                    )
                    Icon(
                        imageVector = Svg.Chevron_down,
                        contentDescription = null,
                        modifier = Modifier
                            .align(Alignment.CenterVertically)
                            .rotate(animation)
                    )
                }
            }
            DropdownMenu(
                modifier = Modifier.width(320.dp),
                expanded = isMenuExpanded,
                onDismissRequest = { isMenuExpanded = false }) {
                DeviceEditorScope.Wrapper.RotationTouch.all.forEach {
                    DropdownMenuItem(
                        text = {
                            Row {
                                Icon(it.icon, null, Modifier.rotate(it.angle.toFloat()))
                                Space(16.dp)
                                Text(it.name.rememberString())
                            }
                        },
                        onClick = {
                            nnvl.rotationTouch.set(it.type.value)
                            isMenuExpanded = false
                        }
                    )
                }
            }

        }
    }
}

@Composable
fun DeviceEditorScope.VideoScale(
    nnvl: DeviceEditorScope.Wrapper,
    modifier: Modifier
) {
    var isMenuExpanded by remember {
        mutableStateOf(false)
    }
    val animation by animateFloatAsState(
        animationSpec = tween(200),
        targetValue = if (isMenuExpanded) 180F
        else 0F
    )

    @Composable
    fun ResolutionStandard.RowInfo(modifier: Modifier, showChevron: Boolean) {
        Row(
            modifier = modifier,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Text(text = videoScaleName.rememberString())
            if (this@RowInfo != ResolutionStandard.UNDEFINED) {
                Text(
                    if (DeviceEditorScope.Wrapper.RotationDisplay[nnvl.rotationDisplay.state].isLand) {
                        "(${width} x ${height})"
                    } else {
                        "(${height} x ${width})"
                    }
                )
            }
            if (showChevron) {
                Spacer(Modifier.weight(1F))
                Icon(
                    imageVector = Svg.Chevron_down,
                    contentDescription = null,
                    modifier = Modifier
                        .align(Alignment.CenterVertically)
                        .rotate(animation)
                )
            }
        }
    }
    EditorCardWrapper(
        icon = Svg.Video,
        title = Res.string.device_detail__video_scale_title,
        tooltip = Res.string.device_detail__video_scale_tooltip,
        modifier = modifier
    ) {
        ElevatedCard(
            enabled = nnvl.canEditDisplay,
            onClick = { isMenuExpanded = !isMenuExpanded },
            colors = CardDefaults.elevatedCardColors(
                containerColor = MaterialTheme.colorScheme.surfaceContainerHigh
            )
        ) {
            ResolutionStandard[nnvl.videoScale.state].RowInfo(Modifier.fillMaxWidth().padding(16.dp), true)
            DropdownMenu(
                modifier = Modifier.width(320.dp),
                expanded = isMenuExpanded,
                onDismissRequest = { isMenuExpanded = false }) {
                ResolutionStandard.all.forEach {
                    DropdownMenuItem(
                        text = {
                            it.RowInfo(Modifier, false)
                        },
                        onClick = {
                            nnvl.videoScale.set(it.mask)
                            isMenuExpanded = false
                        }
                    )
                }
            }

        }
    }
}

@Composable
fun DeviceEditorScope.RotationScreen(
    nnvl: DeviceEditorScope.Wrapper,
    modifier: Modifier
) {
    var isMenuExpanded by remember {
        mutableStateOf(false)
    }
    val animation by animateFloatAsState(
        animationSpec = tween(200),
        targetValue = if (isMenuExpanded) 180F
        else 0F
    )
    EditorCardWrapper(
        icon = Svg.Aspect_ratio,
        title = Res.string.device_detail__display__rotation_title,
        tooltip = Res.string.device_detail__display__rotation_tooltip,
        modifier = modifier
    ) {
        ElevatedCard(
            enabled = nnvl.canEditDisplay,
            onClick = { isMenuExpanded = !isMenuExpanded },
            colors = CardDefaults.elevatedCardColors(
                containerColor = MaterialTheme.colorScheme.surfaceContainerHigh
            )
        ) {
            Row(Modifier.fillMaxWidth().padding(16.dp)) {
                with(DeviceEditorScope.Wrapper.RotationDisplay.all[nnvl.rotationDisplay.state]) {
                    Icon(icon, "", Modifier.rotate(angle.toFloat()))
                    Space(16.dp)
                    Text(
                        text = shortcut.rememberString(),
                        modifier = Modifier.weight(1F)
                    )
                    Icon(
                        imageVector = Svg.Chevron_down,
                        contentDescription = null,
                        modifier = Modifier
                            .align(Alignment.CenterVertically)
                            .rotate(animation)
                    )
                }
            }
            DropdownMenu(
                modifier = Modifier.width(320.dp),
                expanded = isMenuExpanded,
                onDismissRequest = { isMenuExpanded = false }) {
                DeviceEditorScope.Wrapper.RotationDisplay.all.forEach {
                    DropdownMenuItem(
                        text = {
                            Row {
                                Icon(it.icon, null, Modifier.rotate(it.angle.toFloat()))
                                Space(16.dp)
                                Text(it.name.rememberString())
                            }
                        },
                        onClick = {
                            val isLastLand = nnvl.rotationDisplay.state in arrayOf(0, 2)
                            val isNewLand = it.value in arrayOf(0, 2)
                            val isCustomResolution = ResolutionStandard.UNDEFINED == ResolutionStandard.find(nnvl.data.config.display)
                            nnvl.rotationDisplay.set(it.value)
                            isMenuExpanded = false
                            if (isLastLand != isNewLand && !isCustomResolution) {
                                val lastW = nnvl.screenWidth.state
                                nnvl.screenWidth.set(TextFieldValue(nnvl.screenHeight.state.toString()))
                                nnvl.screenHeight.set(TextFieldValue(lastW.toString()))
                            }
                        }
                    )
                }
            }
        }
    }
}

@Composable
fun DeviceEditorScope.DevicePhoto(nnvl: DeviceEditorScope.Wrapper, modifier: Modifier) {

    EditorCardWrapper(
        icon = Svg.Image,
        title = Res.string.device_detail__info__photo_title,
        tooltip = Res.string.device_detail__info__photo_tooltip,
        modifier = modifier
    ) {
        ElevatedCard(
            colors = CardDefaults.elevatedCardColors(
                containerColor = MaterialTheme.colorScheme.surfaceContainerHigh
            )
        ) {
            Box(Modifier.fillMaxWidth()) {
                LazyImage(
                    id = nnvl.photo.state,
                    contentScale = ContentScale.Fit,
                    modifier = Modifier.fillMaxWidth()
                        .defaultMinSize(minHeight = 128.dp)
                )
                AddIconAction(
                    companyId = nnvl.companyId.state,
                    entryId = args.deviceId,
                    canEdit = true,
                    image = nnvl.photo,
                    small = false,
                    orientation = ResourceOrientation.UNDEFINED,
                    extraKeyImage = "device:photo:${args.deviceId}",
                    canBeIcon = false,
                    modifier = Modifier.align(Alignment.BottomEnd)
                )
            }
        }
    }
}

@Composable
fun DeviceEditorScope.Locality(nnvl: DeviceEditorScope.Wrapper, modifier: Modifier) {
    val item: @Composable (label: String, enabled: Boolean, onClick: () -> Unit) -> Unit = { label, enabled, callback ->
        ElevatedCard(
            onClick = callback,
            enabled = enabled,
            colors = CardDefaults.elevatedCardColors(
                containerColor = MaterialTheme.colorScheme.surfaceContainerHigh
            )
        ) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(16.dp),
                modifier = Modifier.padding(8.dp)
            ) {
                Text(
                    text = label,
                    modifier = Modifier.weight(1F)
                )
                if (enabled) Icon(Svg.Chevron_right, null)
            }
        }
    }

    val companyDialog = DialogState.remember()
    val locationDialog = DialogState.remember()

    LocationFilterDialog(
        title = Res.string.device_detail__locality__picker_company.rememberString(),
        state = companyDialog,
        items = { nnvl.data.companyBundle.items },
        onSelect = {
            nnvl.companyId.set(it.id)
            if (it.location.none { it.id == nnvl.locationId.state }) {
                nnvl.locationId.set(it.location.firstOrNull()?.id ?: -1)
            }
        }
    )

    LocationFilterDialog(
        title = Res.string.device_detail__locality__picker_location.rememberString(),
        state = locationDialog,
        items = { nnvl.data.companyBundle.items.firstOrNull { it.id == nnvl.companyId.state }?.location ?: listOf() },
        onSelect = {
            nnvl.locationId.set(it.id)
        }
    )

    EditorCardWrapper(
        icon = Svg.Store,
        title = Res.string.device_detail__locality__label,
        tooltip = Res.string.device_detail__locality__tooltip,
        modifier = modifier
    ) {
        Column(Modifier.fillMaxWidth()) {
            nnvl.data.companyBundle.items
                .firstOrNull { it.id == nnvl.companyId.state }
                ?.also { company ->
                    item(company.name, nnvl.canEditCompany) {
                        companyDialog.show()
                    }
                    company.location
                        .firstOrNull { it.id == nnvl.locationId.state }
                        ?.also { location ->
                            item(location.name, nnvl.canEditLocation) {
                                locationDialog.show()
                            }
                        }
                }
        }

    }
}

@Composable
fun DeviceEditorScope.Volume(nnvl: DeviceEditorScope.Wrapper, modifier: Modifier) {
    EditorCardVolumeSetup(
        field = nnvl.volume,
        tooltip = Res.string.device_detail__info__volume_tooltip,
        modifier = modifier
    )
}

@Composable
fun DeviceEditorScope.Layers(nnvl: DeviceEditorScope.Wrapper, modifier: Modifier) {
    EditorCardWrapper(
        icon = Svg.Layers,
        title = Res.string.device_detail__layer__label,
        tooltip = Res.string.device_detail__layer__tooltip,
        modifier = modifier
    ) {
        SwitchCard(
            field = nnvl.layerTop,
            label = Res.string.device_detail__layer__top_title,
            enabled = nnvl.canEditLayers
        )
        SwitchCard(
            field = nnvl.layerBottom,
            label = Res.string.device_detail__layer__bottom_title,
            enabled = nnvl.canEditLayers
        )
    }
}

@Composable
fun DeviceEditorScope.DefaultKioskApp(
    nnvl: DeviceEditorScope.Wrapper,
    modifier: Modifier
) {
    var isMenuExpanded by remember {
        mutableStateOf(false)
    }
    val animation by animateFloatAsState(
        animationSpec = tween(200),
        targetValue = if (isMenuExpanded) 180F
        else 0F
    )
    EditorCardWrapper(
        icon = Svg.Square_rounded_badge_outline,
        title = Res.string.device_detail__default_app_title,
        tooltip = Res.string.device_detail__default_app_tooltip,
        modifier = modifier
    ) {
        ElevatedCard(
            onClick = { isMenuExpanded = !isMenuExpanded },
            colors = CardDefaults.elevatedCardColors(
                containerColor = MaterialTheme.colorScheme.surfaceContainerHigh
            )
        ) {
            Row(Modifier.fillMaxWidth().padding(16.dp)) {

                with(nnvl.defaultAppLayerApps.firstOrNull { it.id == nnvl.defaultKioskAppId.state } ?: nnvl.defaultAppLayerApps.first()) {
                    LazyImage(image, Modifier.size(24.dp).align(Alignment.CenterVertically), alphaBackground = false)
                    Space(16.dp)
                    Text(
                        text = label,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1F)
                            .align(Alignment.CenterVertically)
                    )
                    Icon(
                        imageVector = Svg.Chevron_down,
                        contentDescription = null,
                        modifier = Modifier
                            .align(Alignment.CenterVertically)
                            .rotate(animation)
                    )
                }
            }
            DropdownMenu(
                modifier = Modifier.width(320.dp),
                expanded = isMenuExpanded,
                onDismissRequest = { isMenuExpanded = false }) {
                nnvl.defaultAppLayerApps.forEach {
                    DropdownMenuItem(
                        text = {
                            Row {
                                LazyImage(
                                    id = it.image,
                                    modifier = Modifier.size(24.dp)
                                        .align(Alignment.CenterVertically),
                                    alphaBackground = false
                                )
                                Space(16.dp)
                                Text(
                                    text = it.label,
                                    maxLines = 2,
                                    overflow = TextOverflow.Ellipsis,
                                    modifier = Modifier.align(Alignment.CenterVertically),
                                )
                            }
                        },
                        onClick = {
                            nnvl.defaultKioskAppId.set(it.id)
                            isMenuExpanded = false
                        }
                    )
                }
            }
        }
    }
}

@Composable
fun DeviceEditorScope.Cursor(nnvl: DeviceEditorScope.Wrapper, modifier: Modifier) {
    EditorCardWrapper(
        icon = Svg.Currency_sign,
        title = Res.string.device_detail__cursor_visible_title,
        tooltip = Res.string.device_detail__cursor_visible_message,
        modifier = modifier
    ) {
        SwitchCard(
            field = nnvl.showCursor,
            label = Res.string.device_detail__cursor_visible_message
        )
    }
}

@Composable
fun DeviceEditorScope.GeoLocation(nnvl: DeviceEditorScope.Wrapper, modifier: Modifier) {
    val externals = LocalExternalsProvider.current
    val scope = rememberCoroutineScope()
    EditorCardWrapper(
        icon = Svg.Map_marker_multiple,
        title = Res.string.device_detail__info__geo_location,
        tooltip = Res.string.device_detail__info__geo_location_tooltip,
        modifier = modifier
    ) {
        fun makeGeoSplit(input: String): Boolean {
            val parts = input.split(", ")
            val lat = parts.getOrNull(0)?.trim()?.toDoubleOrNull()
            val lon = parts.getOrNull(1)?.trim()?.toDoubleOrNull()
            return if (lat != null && lon != null) {
                nnvl.lat.set(TextFieldValue(parts[0].trim()))
                nnvl.lon.set(TextFieldValue(parts[1].trim()))
                true
            } else false
        }
        Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
            TextField(
                shape = RoundedCornerShape(12.dp),
                value = nnvl.lat.text,
                label = { Text(Res.string.device_detail__info__geo_location_lat.rememberString()) },
                isError = nnvl.lat.err,
                modifier = Modifier.weight(1F),
                onValueChange = {
                    if (!makeGeoSplit(it.text)) {
                        nnvl.lat.set(it)
                    }
                })
            TextField(
                shape = RoundedCornerShape(12.dp),
                value = nnvl.lon.text,
                label = { Text(Res.string.device_detail__info__geo_location_lon.rememberString()) },
                isError = nnvl.lon.err,
                modifier = Modifier.weight(1F),
                onValueChange = {
                    if (!makeGeoSplit(it.text)) {
                        nnvl.lon.set(it)
                    }
                })

            if (externals.isGeolocationAvailable()) {
                //https://www.google.com/maps/@48.1525579,17.1382983,107m
                //https://www.google.com/maps/@48.1515893,17.1382983,127a,35y,39.47t
                SmallFloatingActionButton(
                    modifier = Modifier.align(Alignment.CenterVertically),
                    onClick = {
                        scope.launch {
                            externals.getGeoLocation()?.also {
                                nnvl.lat.set(TextFieldValue(it.first.toString()))
                                nnvl.lon.set(TextFieldValue(it.second.toString()))
                            } ?: run {
                                snackBarStateGlobal.showSnackbar("Fail to check geo-location")
                            }
                        }
                    }) { Icon(Svg.Marker, null) }
            }
        }
    }
}

@Composable
fun DeviceEditorScope.Title(nnvl: DeviceEditorScope.Wrapper, modifier: Modifier) {
    TextField(
        value = nnvl.name.text,
        isError = nnvl.name.err,
        onValueChange = { nnvl.name.set(it) },
        modifier = modifier.clip(CardDefaults.shape),
        leadingIcon = { Icon(Svg.Format_text, "") },
        label = { Text(Res.string.device_detail__info__name_hint.rememberString()) },
        shape = CardDefaults.shape
    )
}


@Composable
private fun DeviceEditorScope.DisplayEditor(
    nnvl: DeviceEditorScope.Wrapper,
    modifier: Modifier,
) {

    var isMenuExpanded by remember {
        mutableStateOf(false)
    }
    Row(modifier) {
        Box(Modifier.width(100.dp).align(Alignment.CenterVertically)) {
            Button(
                enabled = nnvl.canEditDisplay,
                onClick = { isMenuExpanded = true },
            ) {
                Text(
                    nnvl.screenResolution.value.nameShort.rememberString(),
                    fontWeight = FontWeight.Black
                )
            }
            DropdownMenu(
                expanded = isMenuExpanded,
                onDismissRequest = { isMenuExpanded = false }) {
                ResolutionStandard.all.forEach {
                    DropdownMenuItem(
                        text = { Text(it.nameLong.rememberString()) },
                        onClick = {
                            nnvl.screenResolution.value = it
                            if (it != ResolutionStandard.UNDEFINED) {
                                val isLand = nnvl.rotationDisplay.state in arrayOf(0, 2)
                                if (isLand) {
                                    nnvl.screenWidth.set(TextFieldValue(it.width.toString()))
                                    nnvl.screenHeight.set(TextFieldValue(it.height.toString()))
                                } else {
                                    nnvl.screenWidth.set(TextFieldValue(it.height.toString()))
                                    nnvl.screenHeight.set(TextFieldValue(it.width.toString()))
                                }
                            }
                            isMenuExpanded = false
                        }
                    )
                }
            }
        }
        Space(24.dp)
        TextField(
            maxLines = 1,
            singleLine = true,
            modifier = Modifier.weight(1F),
            value = nnvl.screenWidth.text,
            enabled = nnvl.canEditDisplay && nnvl.screenResolution.value is ResolutionStandard.UNDEFINED,
            readOnly = nnvl.screenResolution.value !is ResolutionStandard.UNDEFINED,
            isError = nnvl.screenWidth.err,
            label = { Text(Res.string.device_detail__display__resolution_width.rememberString()) },
            onValueChange = {
                nnvl.screenWidth.set(it)
            }
        )
        Space(8.dp)
        Text(
            text = "X",
            style = MaterialTheme.typography.titleLarge,
            modifier = Modifier.align(Alignment.CenterVertically)
        )
        Space(8.dp)
        TextField(
            maxLines = 1,
            singleLine = true,
            modifier = Modifier.weight(1F),
            value = nnvl.screenHeight.text,
            enabled = nnvl.canEditDisplay && nnvl.screenResolution.value is ResolutionStandard.UNDEFINED,
            readOnly = nnvl.screenResolution.value !is ResolutionStandard.UNDEFINED,
            isError = nnvl.screenHeight.err,
            label = { Text(Res.string.device_detail__display__resolution_height.rememberString()) },
            onValueChange = {
                nnvl.screenHeight.set(it)
            }
        )
    }
}


@Composable
private fun <T : PortalIO.EntryFilter> LocationFilterDialog(
    title: String,
    state: DialogState,
    items: () -> List<T>,
    onSelect: (T) -> Unit
) {
    Dialog(state) {

        val search = remember { mutableStateOf(TextFieldValue()) }

        val contentHeight = remember {
            minOf(512, items().size * 48).dp
        }
        DialogUI(
            modifier = Modifier.align(Alignment.Center),
            title = { Text(title) },
            onDismissRequest = { state.hide() },
            dismissButton = {
                TextButton({ state.hide() }) {
                    Text(Res.string.dialog__action_cancel.rememberString())
                }
            }) {
            Column(modifier = Modifier.width(384.dp)) {
                SearchUI(
                    filed = search,
                    modifier = Modifier.fillMaxWidth()
                )
                Space(16.dp)
                LazyColumn(
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                    modifier = Modifier.fillMaxWidth()
                        .height(contentHeight)
                ) {
                    items(
                        SearchWrapper.use(
                            fields = arrayOf(PortalIO.EntryFilter::name),
                            items = items(),
                            query = search,
                            sort = compareBy { it.name }
                        )) { item ->
                        Card(
                            Modifier
                                .fillMaxWidth()
                                .clip(CardDefaults.shape)
                                .clickable {
                                    @Suppress("UNCHECKED_CAST")
                                    onSelect(item as T)
                                    state.hide()
                                },
                        ) {
                            Text(
                                text = item.name,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis,
                                modifier = Modifier.fillMaxWidth()
                                    .padding(8.dp)
                            )
                        }
                    }
                }
            }
        }
    }
}


@Composable
private fun HardwareEntry(
    largeImage: Boolean,
    imageRes: Long,
    title: String,
    subtitle: String
) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        modifier = Modifier.padding(8.dp)
    ) {
        if (largeImage) {
            Column(Modifier.fillMaxWidth()) {
                LazyImage(
                    id = imageRes,
                    alphaBackground = false,
                    modifier = Modifier.size(256.dp)
                )
                Text(
                    text = title,
                    style = MaterialTheme.typography.bodyLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                if (subtitle.isNotEmpty()) {
                    Text(
                        text = subtitle,
                        style = MaterialTheme.typography.bodySmall,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }
        } else {
            if (imageRes > 0) {
                LazyImage(
                    id = imageRes,
                    modifier = Modifier.size(128.dp)
                )
            } else {
                Icon(Svg.Device, null, Modifier.size(64.dp))
            }
            Column(
                Modifier.align(Alignment.CenterVertically)
                    .fillMaxWidth()
            ) {
                Text(
                    text = title,
                    style = MaterialTheme.typography.bodyLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                if (subtitle.isNotEmpty()) {
                    Text(
                        text = subtitle,
                        style = MaterialTheme.typography.bodySmall,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }
        }

    }
}